import { keyframes, styled } from 'theme'
import {
  Grid,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  TableBody as bodyTable,
  MenuList as menuList,
  Paper as paper,
  Popper,
  Table as MainTable,
} from '@mui/material'
import { Box, Flex } from 'components/elements'
import { Link } from 'react-router-dom'
import { h5Styles, paraStyles } from 'components/elements/styleText'

// --- Keyframes ---
export const pop = keyframes({
  '0%': {
    transform: 'scale(1)',
    boxShadow: 'var(--box-shadow)',
  },
  '100%': {
    transform: 'scale(var(--scale))',
    boxShadow: 'var(--box-shadow-picked-up)',
  },
})

export const fadeIn = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
})

export const shimmer = keyframes({
  '0%': {
    backgroundPosition: '-1000px 0px',
  },
  '100%': {
    backgroundPosition: '1000px 0px',
  },
})

export const GridBox = styled(Grid, {
  '&.MuiGrid-root': {
    'mt': 0,
    '&.MuiGrid-item': {
      pt: 0,
    },
  },
})
export const Span = styled('span', {})
export const MenuList = styled(menuList, {
  '&.MuiList-root': {
    position: 'static',
    overflow: 'auto',
    p: 0,
  },
})
export const Paper = styled(paper, {})
export const LinkItem = styled(Link, {
  display: 'flex',
  transitionButton: '$transition$easeOutQuint',
  variants: {
    color: {
      primary: {
        padding: '$space$6 $space$14',
        background: 'transparent',
        color: '$gray500',
        borderRadius: '$radii$xs',
      },
      textBlack: {
        color: '$blackBase',
      },
      textGray: {
        color: '$gray300',
      },
    },
    active: {
      true: {},
    },
  },
  compoundVariants: [
    {
      color: 'primary',
      active: true,
      css: {
        'background': '$primaryBg',
        'color': '$whiteBase',
        '&:hover': {
          color: '$whiteBase',
          background: '$primaryBgHover',
        },
      },
    },
    {
      color: 'textBlack',
      active: true,
      css: {
        color: '$primaryBase',
      },
    },
    {
      color: 'textGray',
      active: true,
      css: {
        color: '$primaryBase',
      },
    },
  ],
  defaultVariants: {
    active: 'false',
  },
})
export const TableItemRow = styled(TableRow, {
  '&.MuiTableRow-root': {
    '&.Mui-selected': {
      'background': '$gray50',
      '&:hover': {
        background: '$primary25',
      },
    },
    '.collapse-button': {
      paddingRight: '0px !important',
    },
    '.collapse_column': {
      padding: '0px !important',
    },
    '&:last-child': {
      td: {
        borderBottom: 'none ',
      },
    },
  },
})

export const scrollBar = {
  '&::-webkit-scrollbar': {
    width: 12,
    height: 12,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: 0,
    my: '$space$8',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '$gray300',
    width: 8,
    borderRadius: 8,
  },
}
export const scrollBarThin = {
  '&::-webkit-scrollbar': {
    width: 8,
    height: 12,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: 0,
    my: '$space$8',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '$gray300',
    width: 8,
    borderRadius: 8,
  },
}
export const TableContainer = styled(Box, {
  width: '100%',
  overflow: 'auto',
  // borderRadius: '0 0 $radii$md $radii$md',
  borderRadius: 'inherit',
  ...scrollBar,
})
export const Table = styled(MainTable, {
  width: '100%',
  borderCollapse: 'collapse',
  borderSpacing: 0,
  minWidth: 1200,
})
export const TableHeader = styled(TableHead, {
  backgroundColor: '$whiteBase',
  borderBottom: '1px solid',
  borderColor: '$gray200',
})
export const TableTh = styled(TableCell, {
  '&.MuiTableCell': {
    '&-root': {
      'p': '$space$10 $space$20',
      'borderBottom': '1px solid',
      'borderColor': '$gray200',
      '&:first-child:not(.collapse_column), &:last-child': {
        px: '$space$15', // Add padding-left and padding-right
      },
    },
    '&-head': {
      'zIndex': 3,
      'fontSize': '$12',
      'fontWeight': '$medium',
      'lineHeight': '$lineHeight$18',
      'fontFamily': '$Inter',
      'color': '$gray900',
      '&:hover': {
        color: '$gray900',
        svg: {
          opacity: 0.5,
        },
      },
      'span': {
        color: '$gray900',
      },
    },
  },
  '.Mui': {
    '&ButtonBase': {
      '&-root': {
        'fontSize': '$12',
        'fontWeight': '$medium',
        'lineHeight': '$lineHeight$18',
        'fontFamily': '$Inter',
        'color': '$gray600',
        '&:hover': {
          color: '$gray700',
          svg: {
            opacity: 0.5,
          },
        },
        '&.Mui-active': {
          'color': '$primaryBase',
          'svg': {
            opacity: 1,
          },
          '&.isAscending': {
            svg: {
              transform: 'rotate(180deg)',
            },
          },
        },
      },
    },
    '&SvgIcon': {
      '&-root': {
        opacity: 0,
        transition: 'transform 0.6s $transition$easeOutQuint',
      },
    },
  },
})
export const TableBody = styled(bodyTable, {
  '&.MuiTableBody-root': {
    '.MuiTableRow-root': {
      '&:last-child': {
        borderBottom: 0,
        // borderBottomLeftRadius: '$radii$md',
        // borderBottomRightRadius: '$radii$md',
      },
    },
  },
})
export const TableTd = styled(TableCell, {
  '&.MuiTableCell-root': {
    ...paraStyles,
    'fontFamily': '$Inter',
    'borderBottom': '1px solid',
    'borderColor': '$gray200',
    'p': '$space$10 $space$20',
    '&:first-child, &:last-child': {
      px: '$space$15', // Add padding-left and padding-right
    },
    '&.collapse_column': {
      backgroundColor: '$whiteBase',
    },
  },
})
export const Header = styled('header', {
  background: '$whiteBase',
  p: '$space$10',
  boxShadow: '$headerShadow',
})
export const Footer = styled('footer', {
  a: { pr: '$space$10' },
})
export const List = styled('li', {
  ...paraStyles,
  pb: '$space$6',
  counterIncrement: 'section',
})
export const UnorderList = styled('ul', {
  m: 0,
  pl: 0,
})
export const FlexableTabs = styled(Tabs, Flex, {
  '.MuiTabs': {
    '&-scrollButtons': {
      'width': 20,
      '&:first-child': {
        mr: '$space$10',
      },
      '&:last-child': {
        ml: '$space$10',
      },
      '&.Mui-disabled': {
        opacity: 0.35,
      },
    },
    '&-indicator': {
      display: 'none',
    },
  },
})
export const FlexTab = styled(Tab, Flex, {
  '&.MuiButtonBase-root': {
    'fontFamily': '$Inter',
    'minHeight': 'auto',
    'textTransform': 'capitalize',
    '&:before': {
      display: 'none',
    },
  },
})
export const OrderList = styled('ol', {
  m: 0,
  pl: '$space$16',
  counterReset: 'section',
  li: {
    'listStyle': 'none',
    '&:before': {
      content: 'counters(section, ".") ". "',
    },
  },
})
export const CircleBox = styled(Box, {
  width: 8,
  height: 8,
  background: '$disabledIcon',
  borderRadius: '$radii$rounded',
})

export const overflowText = {
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}
export const DropdownWrap = {
  mt: '$space$4',
  border: '1px solid',
  borderColor: '$border',
  borderRadius: '$xs',
  boxShadow: '$dropdownBoxShadow',
  backgroundColor: '$whiteBase',
}
export const DropdownList = {
  ...h5Styles,
  'cursor': 'pointer',
  'color': '$inputText',
  'textAlign': 'left',
  'mb': '$space$2',
  '&:hover': {
    backgroundColor: '$gray50',
  },
}
export const WhiteContainer = {
  background: '$whiteBase',
  p: '$space$14 $space$20',
  borderRadius: '$radii$md',
}
export const BorderBackgroundCss = {
  border: '1px solid',
  borderColor: '$border',
  borderRadius: '$radii$md',
  background: '$whiteBase',
}
export const LineClamp = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}
export const DropdownCss = {
  'mb': '$space$20',
  '&.dropdown': {
    // 'position': 'relative',
    'button': {
      justifyContent: 'space-between',
      pr: '$size$12',
      transition: 'all 0.6s $transition$easeOutQuint',
      svg: {
        transition: 'transform 0.6s $transition$easeOutQuint',
      },
    },
    'button:not(.create-button), label': {
      width: '100%',
    },
    '&Hide': {
      position: 'absolute',
      opacity: 0,
      PointerEvent: 'none',
      zIndex: '-10',
    },
    '.base': {
      '&-Popper': {
        '&-root': {
          // width: '100%',
          zIndex: 10,
        },
      },
    },
    '.Mui': {
      '&Paper': {
        '&-root': {
          ...DropdownWrap,
        },
      },
      '&List': {
        '&-root': {
          'maxHeight': 250,
          'overflowY': 'auto',
          ...scrollBar,
          '&::-webkit-scrollbar': {
            width: 6,
          },
        },
      },
    },
    '[aria-expanded="true"]': {
      'borderColor': '$dropdownBorderFocus',
      'boxShadow': '$inputShadow',
      '.MuiSvgIcon': {
        '&-root': {
          transform: 'rotate(180deg)',
        },
      },
    },
  },
  '.dropdown': {
    '&List': {
      ...DropdownList,
      pb: 0,
    },
  },
}
export const TooltipWrap = styled(Tooltip, {
  '.MuiTooltip-tooltip': {
    backgroundColor: 'green',
  },
})

export const PopperWrap = styled(Popper, {})
export const GroupIconCss = {
  borderWidth: 8,
  borderStyle: 'solid',
  p: '$space$6',
  borderRadius: '$radii$rounded',
}
export const TabButtonCss = {
  ...h5Styles,
  p: '$space$10 $space$16',
  minHeight: 44,
  minWidth: 170,
  borderRadius: '$radii$sm',
  flexDirection: 'row',
}
export const BorderWrapCss = {
  backgroundColor: '$whiteBase',
  borderRadius: 'inherit',
  // borderBottomLeftRadius: '$radii$md',
  // borderBottomRightRadius: '$radii$md',
  height: '100%',
  zIndex: 1,
}
export const CellCss = {
  flexGrow: 1,
}

export const HeaderCellCss = {
  p: '$space$14 $space$20',
}

export const BodyCellCss = {
  p: '$space$16 $space$20',
}

export const ButtonGroupDivCss = {
  alignItems: 'center',
  button: {
    alignItems: 'initial',
  },
}

export const TableRowCss = {
  'display': 'flex',
  'borderTop': '1px solid $border',
  'p': 0,
  'zIndex': 'auto !important',
  'backgroundColor': '$gray50',
  '&:hover': {
    background: '$primary25',
  },
}

export const TagOverFlowCss = {
  display: 'inline-block',
  mr: '$space$8',
  maxWidth: 150,
  ...overflowText,
}

export const TableCss = {
  'border': 'none',
  '.MuiTableCell-root': {
    borderTop: 0,
  },
  'th': {
    borderRadius: 'none',
  },
}

export const inspectionTableCss = {
  'th': {
    borderRadius: 'none',
  },
  'tr > td': {
    background: '$whiteBase !important',
  },
  '.MuiTableRow-root:last-child .MuiTableCell-root': {
    borderTop: '1px solid $border',
  },
}

export const TableStyling = {
  'border': 'none',
  'borderRadius': '$radii$md',
  '.MuiTableCell-root': {
    borderTop: 0,
  },
  'th:first-child': {
    borderTopLeftRadius: '$radii$md',
  },
  'th:last-child': {
    borderTopRightRadius: '$radii$md',
  },
}

export const ItemCenterCss = {
  '&.MuiTableCell-root': {
    textAlign: 'center',
    width: 150,
  },
}

export const TableRowItemCss = {
  // 'cursor': 'pointer',
  'backgroundColor': '$gray50',
  'borderBottom': '1px solid',
  'borderColor': '$gray200',
  '&:hover': {
    background: '$primary25',
  },
}

export const PaginationWrapCss = {
  '.MuiTablePagination-actions': {
    display: 'block',
  },
  'px': '$space$15',
  // 'm': 0,
}
export const InputStartCss = {
  backgroundColor: '$gray200',
  borderRight: '1px solid $gray300',
  borderRadius: '$radii$sm',
  minWidth: 30,
  mr: '$8',
}
export const Badge = styled(Flex, {
  minWidth: '$space$16',
  height: '$space$20',
  maxWidth: '$space$30',
  px: '$space$2',
  borderRadius: '$radii$md',
  backgroundColor: '$primaryBase',
  color: '$whiteBase',
  fontSize: '$12',
  fontWeight: '$bold',
})
export const filtersDropdownCss = {
  mr: '$space$15',
  minWidth: 190,
  mb: '0 !important',
}
export const LoaderDivCss = {
  position: 'absolute',
  background: '$buttonDisabledBorder',
  zIndex: 1,
  opacity: 0.5,
  borderRadius: '$radii$sm',
  top: 0,
}
